import {
  SwapperStyles,
  ThemeMode,
  BrandTheme,
  ComponentStyles,
} from "@src/models";
import { isValidColor, validateColors } from "@src/utils";

function setCustomProperty(property: string, value: string) {
  function applyStyleToHost(hostId: string) {
    const host = document.getElementById(hostId);
    if (host && host.shadowRoot) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(`:host { ${property}: ${value}; }`);
      host.shadowRoot.adoptedStyleSheets = [
        ...host.shadowRoot.adoptedStyleSheets,
        sheet,
      ];
    }
  }

  const hostIds = [
    "swapper-sdk-root",
    "swapper-sdk-tx-status",
    "swapper-sdk-widget",
  ];

  hostIds.forEach(applyStyleToHost);
}

// Default theme definitions
export const lightThemeBase = {
  primaryColor: "#836FFF",
  secondaryColor: "#5F4DD0",
  backgroundColor: "#F0F3FF",
  textColor: "#211951",
  successColor: "#4CAF50",
  warningColor: "#FF6F00",
  errorColor: "#F44336",
  primaryButtonBackground: "#836FFF",
  primaryButtonText: "#FFFFFF",
  secondaryButtonBackground: "#5F4DD0",
  secondaryButtonText: "#836FFF",
  disabledButtonBackground: "#21195180",
  disabledButtonText: "#FFFFFF",
  // UI Elements for light theme
  inputBackground: "#FFFFFF",
  inputBorder: "#E0E6FF",
  cardBackground: "#FFFFFF",
  borderColor: "#E0E6FF",
};

export const darkThemeBase = {
  primaryColor: "#6366F1",
  secondaryColor: "#7d7fe3",
  backgroundColor: "#1A1A1A",
  textColor: "#E8E8E8",
  successColor: "#4CAF50",
  warningColor: "#FFA726",
  errorColor: "#FF5722",
  primaryButtonBackground: "#6366F1",
  primaryButtonText: "#FFFFFF",
  secondaryButtonBackground: "#7d7fe3",
  secondaryButtonText: "#E8E8E8",
  disabledButtonBackground: "#2D2D2D",
  disabledButtonText: "#6B7280",
  // Additional colors for better dark theme support
  inputBackground: "#2D2D2D",
  inputBorder: "#7d7fe3",
  cardBackground: "#262626",
  borderColor: "#7d7fe3",
};

// CSS custom property mapping
const cssVariableMap = {
  // Layout
  backgroundColor: "--swapper-bg-color",
  borderRadius: "--swapper-border-radius",
  width: "--swapper-width",

  // Typography
  textColor: "--swapper-text-color",

  // Buttons
  primaryButtonBackground: "--swapper-primary-button-bg",
  primaryButtonText: "--swapper-primary-button-text",
  secondaryButtonBackground: "--swapper-secondary-button-bg",
  secondaryButtonText: "--swapper-secondary-button-text",
  disabledButtonBackground: "--swapper-disabled-button-bg",
  disabledButtonText: "--swapper-disabled-button-text",

  // Status colors
  successColor: "--swapper-success-color",
  warningColor: "--swapper-warning-color",
  errorColor: "--swapper-error-color",

  // Brand colors (applied to primary/secondary)
  primaryColor: "--swapper-primary-color",
  secondaryColor: "--swapper-secondary-color",

  // Additional UI elements
  inputBackground: "--swapper-input-bg",
  inputBorder: "--swapper-input-border",
  cardBackground: "--swapper-card-bg",
  borderColor: "--swapper-border-color",
} as const;

/**
 * Apply brand theme colors to override base theme colors
 */
function applyBrandTheme(
  baseTheme: ComponentStyles,
  brandTheme: BrandTheme,
): ComponentStyles {
  const themedStyles = { ...baseTheme };

  if (brandTheme.primaryColor && isValidColor(brandTheme.primaryColor)) {
    themedStyles.primaryButtonBackground = brandTheme.primaryColor;
    // You can add more primary color applications here
  }

  if (brandTheme.secondaryColor && isValidColor(brandTheme.secondaryColor)) {
    themedStyles.secondaryButtonBackground = brandTheme.secondaryColor;
    // You can add more secondary color applications here
  }

  return themedStyles;
}

/**
 * Merge component styles with validation
 */
function mergeComponentStyles(
  baseStyles: ComponentStyles,
  componentStyles?: ComponentStyles,
): ComponentStyles {
  if (!componentStyles) {
    return baseStyles;
  }

  // Validate colors in component styles
  const invalidColors = validateColors(componentStyles);
  if (invalidColors.length > 0) {
    console.warn(
      `Invalid colors found in component styles: ${invalidColors.join(", ")}`,
    );
    // Filter out invalid colors
    const validStyles = { ...componentStyles };
    invalidColors.forEach((key) => {
      delete validStyles[key as keyof ComponentStyles];
    });
    return { ...baseStyles, ...validStyles };
  }

  return { ...baseStyles, ...componentStyles };
}

/**
 * Main function to apply Swapper styles with 3-tier system
 */
export function applySwapperStyles(styles?: SwapperStyles) {
  // Level 1: Theme Mode (default to light)
  const themeMode: ThemeMode = styles?.themeMode || "light";
  const baseTheme = themeMode === "light" ? lightThemeBase : darkThemeBase;

  // Level 2: Brand Theme (override base theme colors)
  const brandThemedStyles = styles?.brandTheme
    ? applyBrandTheme(baseTheme, styles.brandTheme)
    : baseTheme;

  // Level 3: Component Styles (highest priority)
  const finalStyles = mergeComponentStyles(
    brandThemedStyles,
    styles?.componentStyles,
  );

  // Apply CSS custom properties
  Object.entries(finalStyles).forEach(([key, value]) => {
    const cssVariable = cssVariableMap[key as keyof typeof cssVariableMap];
    if (cssVariable && value) {
      setCustomProperty(cssVariable, value);
    }
  });

  // Apply brand colors as CSS variables for direct use
  if (
    styles?.brandTheme?.primaryColor &&
    isValidColor(styles.brandTheme.primaryColor)
  ) {
    setCustomProperty(
      cssVariableMap.primaryColor,
      styles.brandTheme.primaryColor,
    );
  }

  if (
    styles?.brandTheme?.secondaryColor &&
    isValidColor(styles.brandTheme.secondaryColor)
  ) {
    setCustomProperty(
      cssVariableMap.secondaryColor,
      styles.brandTheme.secondaryColor,
    );
  }
}

// Legacy function for backwards compatibility during migration
export function changeHostVariableColor(
  isLightMode: boolean | undefined,
  customStyles:
    | {
        borderRadius?: string;
        width?: string;
        [key: string]: string | undefined;
      }
    | undefined,
) {
  console.warn(
    "changeHostVariableColor is deprecated. Use applySwapperStyles instead.",
  );

  // Convert legacy parameters to new format
  const componentStyles: ComponentStyles = {};

  if (customStyles?.borderRadius) {
    componentStyles.borderRadius = customStyles.borderRadius;
  }

  if (customStyles?.width) {
    componentStyles.width = customStyles.width;
  }

  const newStyles: SwapperStyles = {
    themeMode: isLightMode ? "light" : "dark",
    componentStyles:
      Object.keys(componentStyles).length > 0 ? componentStyles : undefined,
  };

  applySwapperStyles(newStyles);
}
